import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, type SearchHit } from "../api/client.js";
import { filingHref } from "../lib/citations.js";
import { shortDate } from "../lib/format.js";

/**
 * The library, searchable by phrase and by meaning at once.
 *
 * **The two halves fail in opposite directions.** Full-text search finds the words typed
 * and misses a paraphrase; vectors find the meaning and lose precision — `Section 355(e)`
 * means something quite different from plain §355, which a vector can call near-identical.
 * So every row says which half found it.
 *
 * **This finds documents, never facts, and the page says so.** A passage that looks like
 * an answer may be another entity's figure, quoted verbatim from the cited document. A
 * ranked list is a place to start reading, not an answer.
 */
export function SearchPage() {
  /**
   * The committed query lives in the URL, so a search can be linked to, reloaded and gone
   * back to; the draft does not, or the history stack would fill with prefixes of a word.
   */
  const [params, setParams] = useSearchParams();
  const q = params.get("q") ?? "";
  const formType = params.get("formType") ?? "";
  const [draft, setDraft] = useState(q);

  const forms = useQuery({ queryKey: ["search", "forms"], queryFn: api.searchForms });

  const results = useQuery({
    queryKey: ["search", q, formType],
    queryFn: () => api.search({ q, limit: 40, ...(formType ? { formType } : {}) }),
    enabled: q.length >= 2,
  });

  const commit = (next: { q?: string; formType?: string }) => {
    const merged = { q: next.q ?? q, formType: next.formType ?? formType };
    const out = new URLSearchParams();
    if (merged.q) out.set("q", merged.q);
    if (merged.formType) out.set("formType", merged.formType);
    setParams(out);
  };

  const items = results.data?.items ?? [];

  return (
    <div className="read">
      <article className="article searchpage">
        <div className="eyebrow">
          <span>Library</span>
        </div>
        <h1>Search</h1>
        <p className="deck">
          Every ingested filing, by phrase and by meaning at once. This finds documents to
          read — it does not establish facts, and a passage that looks like an answer may
          belong to another entity discussed in the same filing.
        </p>

        <form
          className="histfilters"
          onSubmit={(e) => {
            e.preventDefault();
            commit({ q: draft.trim() });
          }}
        >
          <label className="histsearch">
            <span className="k">Phrase or question</span>
            <input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="debt-for-equity exchange, tax-free under Section 355…"
            />
          </label>
          <label>
            <span className="k">Form</span>
            <select
              value={formType}
              onChange={(e) => commit({ formType: e.target.value })}
            >
              <option value="">any</option>
              {(forms.data?.items ?? []).map((f) => (
                <option key={f.formType} value={f.formType}>
                  {f.formType} ({f.filings.toLocaleString()})
                </option>
              ))}
            </select>
          </label>
          <button type="submit" className="iconbtn">
            Search
          </button>
        </form>

        {q.length < 2 && (
          <p className="deck">
            {/*
              The corpus's shape is named because it decides what is worth asking of it: a
              reader who does not know it reads a narrow result as a narrow corpus.
            */}
            Ask it something a filing would <em>say</em> rather than something it would be
            titled. The index is deepest on Form 10 information statements, which is what
            the historical walk fetched.
          </p>
        )}

        {results.isPending && q.length >= 2 && <p className="deck">Searching…</p>}
        {results.error && <p className="err">{(results.error as Error).message}</p>}

        {results.data && (
          <>
            <p className="fin-caveat">
              {items.length === 0
                ? "Nothing matched."
                : `${items.length} result${items.length === 1 ? "" : "s"}, ${
                    results.data.granularity === "passage" ? "by passage" : "by document"
                  }`}
              {/*
                Stated, never implied. A corpus with no embeddings still searches by
                phrase, and a reader whose paraphrase came back empty would otherwise
                conclude the corpus is empty rather than that half the search is down.
              */}
              {!results.data.searched.meaning && (
                <span className="histtrunc"> · {results.data.searched.meaningUnavailable}</span>
              )}
            </p>
            <div className="filings live">
              {items.map((hit) => (
                <SearchResultRow key={`${hit.documentId}:${hit.chunkId ?? "doc"}`} hit={hit} />
              ))}
            </div>
            {items.length === 0 && results.data.searched.meaning && (
              /*
                Zero from both halves is a real answer, so it is said rather than left as
                an empty screen.
              */
              <p className="deck">
                Both halves ran and neither found anything. On this corpus that is a real
                answer: the phrase does not occur and nothing reads as close to it.
              </p>
            )}
          </>
        )}
      </article>
    </div>
  );
}

/**
 * One result.
 *
 * The link carries `doc` and, where the hit has them, the offsets, so the reader opens at
 * the passage. A hit with no offsets opens the document instead: full-text search ranked
 * the document and never located anything inside it.
 */
function SearchResultRow({ hit }: { hit: SearchHit }) {
  const to = useMemo(
    () =>
      filingHref(hit.accession, {
        documentId: hit.documentId,
        charStart: hit.charStart,
        charEnd: hit.charEnd ?? hit.charStart,
      }),
    [hit],
  );

  return (
    <Link className="filing hit" to={to}>
      <span className="ft">{hit.formType ?? "—"}</span>
      <span className="fk">{hit.companyName ?? "unknown filer"}</span>
      <span className="fw">{shortDate(hit.filingDate)}</span>
      <span className="fd">{hit.filename}</span>
      {/*
        Which half found it. A result both halves found is stronger than one either found
        alone, and this lets a reader see that without being handed a blended score to
        trust.
      */}
      <span className="fmatch">
        {hit.matchedBy.includes("phrase") && <span className="mb phrase">phrase</span>}
        {hit.matchedBy.includes("meaning") && <span className="mb meaning">meaning</span>}
      </span>
      <span className="fsnip">{renderSnippet(hit.snippet)}</span>
    </Link>
  );
}

/**
 * The phrase half wraps its match in guillemets — `ts_headline`'s `StartSel`/`StopSel`.
 * Rendered as a mark rather than left as literal « », which reads as a quotation of the
 * whole fragment rather than as the words that matched.
 */
function renderSnippet(snippet: string): React.ReactNode {
  if (!snippet.includes("«")) return snippet;
  return snippet.split(/(«[^»]*»)/g).map((part, i) =>
    part.startsWith("«") && part.endsWith("»") ? (
      <mark key={i}>{part.slice(1, -1)}</mark>
    ) : (
      <span key={i}>{part}</span>
    ),
  );
}
